import React from "react"
import Footer from "@components/Footer"
import Image from "@components/Image"
import { useIntl } from "@components/Intl"
import { Box, Container } from "@components/Layout"
import SEO from "@components/SEO"
import {
  Link as StyledLink,
  Paragraph,
  ParagraphTitle,
  TitleXL,
} from "@components/Typography"
import { ContentPage as ContentPageModel } from "@lib/kontent/models-deprecated/content_page"
import { getKontentImage } from "@lib/kontent"

interface Props {
  collection: ContentPageModel
  altSlug?: string | null
}

const ContentPage: React.FC<Props> = ({ collection, altSlug }) => {
  const { messages, locale } = useIntl()
  const urlSlug = collection.urlSlug.value

  let pathNl: string | null = null
  let pathEn: string | null = null

  if (locale === "nl") {
    pathNl = `/nl/p/${urlSlug}`
    // only set alt lang path if altSlug  exists
    if (altSlug) {
      pathEn = `/en/p/${altSlug}`
    }
  } else {
    pathEn = `/en/p/${urlSlug}`
    if (altSlug) {
      pathNl = `/nl/p/${altSlug}`
    }
  }

  return (
    <>
      <SEO
        title={collection.seoMetaTitle.value}
        description={collection.seoMetaDescription.value}
        imageUrl={getKontentImage(collection.seoMetaImage.value[0]?.url, {
          width: 600,
        })}
        pathNl={pathNl}
        pathEn={pathEn}
      />
      <Box width="100%" pt={120} bg="white" pb="3rem">
        <Container maxWidth="50rem" pr={["0", "5", "7"]}>
          <TitleXL>{collection.pageTitle.value}</TitleXL>
          <Paragraph style={{ marginBottom: "1rem" }}>
            {collection.description.value}
          </Paragraph>
        </Container>

        {collection.linkedItems.value.map((block, key) =>
          block.system.type === "wide_image_block" ? (
            <Box py={[3, "3rem"]} key={key}>
              {block.image.value[0] && (
                <Image
                  variant="wide"
                  src={getKontentImage(block.image.value[0].url, {
                    width: 1200,
                  })}
                  alt={block.image.value[0].description}
                />
              )}
            </Box>
          ) : block.system.type === "title___paragraph_block" ? (
            <Container
              key={key}
              pt={[2, "1rem"]}
              pb={[3, "3rem"]}
              maxWidth="50rem"
            >
              <ParagraphTitle>{block.title.value}</ParagraphTitle>
              <Paragraph style={{ marginBottom: "1rem" }}>
                {block.content.value}
              </Paragraph>
            </Container>
          ) : block.system.type === "image___text_block" ? (
            <Container
              key={key}
              py={[3, "3rem"]}
              maxWidth="50rem"
              display="grid"
              gridColumnGap={[0, "3rem"]}
              gridRowGap={[3, 0]}
              gridTemplateColumns={["1fr", "1fr 1fr"]}
              alignItems="center"
            >
              <Box
                gridRow={["1", "1"]}
                gridColumn={[
                  "inherit",
                  block.layout_options.value[0].name === "text-image"
                    ? "2"
                    : "1",
                ]}
              >
                {block.image.value[0] && (
                  <Image
                    variant="square"
                    src={getKontentImage(block.image.value[0].url, {
                      width: 600,
                    })}
                    alt={block.image.value[0].description}
                  />
                )}
              </Box>
              <Box
                gridRow={["2", "1"]}
                gridColumn={[
                  "inherit",
                  block.layout_options.value[0].name === "text-image"
                    ? "1"
                    : "2",
                ]}
              >
                <ParagraphTitle>{block.title.value}</ParagraphTitle>
                <Paragraph style={{ marginBottom: "1rem" }}>
                  {block.paragraph.value}
                </Paragraph>
                {block.link_label.value && block.link_url.value && (
                  <StyledLink
                    href={block.link_url.value}
                    title={`${messages.global.view} ${block.link_label.value}`}
                  >
                    {block.link_label.value}
                  </StyledLink>
                )}
              </Box>
            </Container>
          ) : (
            <div />
          )
        )}
      </Box>

      <Footer />
    </>
  )
}

export default ContentPage
